import { IndianRupee, TrendingUp, Clock, Wallet, Tag } from "lucide-react";

interface PaymentSummaryBarProps {
  totalValue: number;
  totalDiscount: number;
  totalReceived: number;
  totalPending: number;
  totalAdvance: number;
}

function fmt(n: number) {
  return n.toLocaleString("en-IN");
}

export default function PaymentSummaryBar({
  totalValue,
  totalDiscount,
  totalReceived,
  totalPending,
  totalAdvance,
}: PaymentSummaryBarProps) {
  const paidPercent = totalValue > 0 ? (totalReceived / totalValue) * 100 : 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 min-[480px]:grid-cols-3 min-[900px]:grid-cols-5 sm:gap-2.5">
        <MetricCard
          icon={<IndianRupee className="size-3.5" strokeWidth={2.2} />}
          iconColor="#46a580"
          label="Deal Value"
          value={fmt(totalValue)}
          bgClass="bg-[#f7faf9]"
          borderClass="border-[#e2ede8]"
        />
        <MetricCard
          icon={<Tag className="size-3.5" strokeWidth={2.2} />}
          iconColor="#8b5cf6"
          label="Discount"
          value={fmt(totalDiscount)}
          bgClass="bg-[#f5f3ff]"
          borderClass="border-[#ddd6fe]"
        />
        <MetricCard
          icon={<TrendingUp className="size-3.5" strokeWidth={2.2} />}
          iconColor="#2d7a5a"
          label="Received"
          value={fmt(totalReceived)}
          bgClass="bg-[#f0faf5]"
          borderClass="border-[#c1e1d5]"
        />
        <MetricCard
          icon={<Clock className="size-3.5" strokeWidth={2.2} />}
          iconColor="#c77800"
          label="Pending"
          value={fmt(totalPending)}
          bgClass="bg-[#fffbf5]"
          borderClass="border-[#f0dfc0]"
        />
        <MetricCard
          icon={<Wallet className="size-3.5" strokeWidth={2.2} />}
          iconColor="#4a7cc9"
          label="Advance"
          value={fmt(totalAdvance)}
          bgClass="bg-[#f0f5ff]"
          borderClass="border-[#c5d5f0]"
        />
      </div>

      <div className="h-[5px] rounded-full bg-[#e8eae9] overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#46a580] to-[#2d7a5a] transition-all duration-500"
          style={{ width: `${Math.min(paidPercent, 100)}%` }}
        />
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  iconColor,
  label,
  value,
  bgClass,
  borderClass,
}: {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  value: string;
  bgClass: string;
  borderClass: string;
}) {
  return (
    <div
      className={`flex min-h-[76px] flex-col justify-between gap-1.5 rounded-[12px] ${bgClass} border ${borderClass} px-3 py-2.5`}
    >
      <div className="flex items-center gap-1.5" style={{ color: iconColor }}>
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-[0.4px]">
          {label}
        </span>
      </div>
      <span className="text-[17px] font-bold text-[#1e302a] tabular-nums tracking-[-0.3px] leading-tight">
        {value}
      </span>
    </div>
  );
}
